import { approvalOutcome } from "@phoenix/agent/shared/pendingApproval";

import { LOAD_DATASET_NAVIGATION_CANCEL_ERROR } from "./constants";
import type {
  BindPendingLoadDatasetOptions,
  PendingLoadDataset,
} from "./types";

export function bindPendingLoadDatasetActions({
  pendingLoad,
  resolveDatasetTarget,
  readSelectionRevision,
  applyDatasetSelection,
  emitResult,
  setPendingLoadDataset,
}: BindPendingLoadDatasetOptions): PendingLoadDataset {
  return {
    ...pendingLoad,
    accept: async ({ approvalSource = "user" } = {}) => {
      setPendingLoadDataset(pendingLoad.toolCallId, null);

      if (readSelectionRevision() !== pendingLoad.expectedRevision) {
        emitResult({
          ok: false,
          error:
            "The playground dataset selection changed after this load was proposed, so it can no longer be applied.",
        });
        return;
      }

      // Dataset or split may have been deleted since the proposal — re-resolve before writing ids.
      const resolution = await resolveDatasetTarget(pendingLoad.input);
      if (!resolution.ok) {
        emitResult({ ok: false, error: resolution.error });
        return;
      }
      const resolvedSplitId = resolution.output.splitId;
      const proposedSplitId = pendingLoad.snapshot.splitIds[0] ?? null;
      if (
        resolution.output.datasetId !== pendingLoad.snapshot.datasetId ||
        resolvedSplitId !== proposedSplitId
      ) {
        emitResult({
          ok: false,
          error:
            "The proposed dataset or split changed after this load was proposed, so it can no longer be applied.",
        });
        return;
      }

      applyDatasetSelection(pendingLoad.snapshot);

      emitResult({
        ok: true,
        output: {
          status: "loaded",
          acceptedBy: approvalSource,
          datasetId: pendingLoad.snapshot.datasetId,
          datasetName: pendingLoad.snapshot.datasetName ?? null,
          splitIds: pendingLoad.snapshot.splitIds,
          splitNames: pendingLoad.snapshot.splitNames ?? [],
          message:
            approvalSource === "auto"
              ? "Dataset load auto-approved."
              : "Playground switched to dataset mode.",
          ...approvalOutcome({ decision: "accepted", source: approvalSource }),
        },
      });
    },
    reject: async () => {
      setPendingLoadDataset(pendingLoad.toolCallId, null);
      emitResult({
        ok: true,
        output: {
          status: "rejected",
          message: "User rejected the proposed dataset load.",
          ...approvalOutcome({ decision: "rejected", source: "user" }),
        },
      });
    },
    cancel: async () => {
      setPendingLoadDataset(pendingLoad.toolCallId, null);
      emitResult({ ok: false, error: LOAD_DATASET_NAVIGATION_CANCEL_ERROR });
    },
  };
}
